import {
    Box,
    Card,
    Stack,
    styled,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import columnShape from 'page-sections/operator/final-score/columnShape';
import HeadingArea from 'page-sections/operator/final-score/heading-area';
import { useEffect, useMemo, useState } from 'react';
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));
const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));
const FinalScore = () => {
    const [dataAbstract, setDataAbstract] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
    } = useTable(
        { columns, data: dataAbstract },
        useGlobalFilter,
        usePagination,
        useRowSelect,
    ); // handle change pagination
    const getData = async (pageParam = pageNo, limitParam = pageSize) => {
        await axiosInstance
            .get(`/api/prv/operator-submissions/final-score`, {
                params: {
                    page: pageParam,
                    limit: limitParam,
                },
            })
            .then(({ data }) => {
                const items = Array.isArray(data) ? data : data?.data || [];
                const meta = Array.isArray(data) ? null : data?.meta || null;
                const normalized = items.map((d) => {
                    const judgeScores =
                        d.judgeScores ||
                        (d.scores
                            ? d.scores.map((score, i) => ({
                                  score,
                                  judgeName: `Judge ${i + 1}`,
                              }))
                            : []);
                    return { ...d, judgeScores };
                });
                setDataAbstract(normalized);
                if (meta) {
                    setTotalPages(meta.totalPages || 0);
                } else {
                    setTotalPages(1);
                }
            })
            .catch((err) => console.log(err));
    };
    useEffect(() => {
        getData(1, pageSize);
        // eslint-disable-next-line
    }, []);
    const handleChange = async (_, currentPageNo) => {
        setPageNo(currentPageNo);
        await getData(currentPageNo, pageSize);
    }; // handle change for

    return (
        <Box pt={2} pb={4}>
            <Card
                sx={{
                    py: 2,
                }}
            >
                <Box px={3} sx={12}>
                    <HeadingArea />
                </Box>

                <Scrollbar autoHide={true}>
                    <Table
                        {...getTableProps()}
                        sx={{
                            minWidth: 900,
                        }}
                    >
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, index) => (
                                            <HeadTableCell
                                                key={index}
                                                sx={{
                                                    paddingY: 0,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    borderBottom: 0,
                                                    width: column.width,
                                                    color: 'text.secondary',
                                                    minWidth: column.minWidth,
                                                    maxWidth: column.maxWidth,
                                                    ...(typeof column.Header === 'string' &&
                                                    ['Judge 1', 'Judge 2', 'Judge 3', 'Final Score'].includes(
                                                        column.Header,
                                                    )
                                                        ? { textAlign: 'center' }
                                                        : {}),
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {page.length === 0 ? (
                                <TableRow>
                                    <BodyTableCell
                                        colSpan={
                                            headerGroups?.[0]?.headers?.length ||
                                            columns.length
                                        }
                                        sx={{ textAlign: 'center' }}
                                    >
                                        No scoring results
                                    </BodyTableCell>
                                </TableRow>
                            ) : (
                                page.map((row, index) => {
                                    prepareRow(row);
                                    return (
                                        <TableRow
                                            {...row.getRowProps()}
                                            key={index}
                                        >
                                            {row.cells.map((cell, index) => {
                                                return (
                                                    <BodyTableCell
                                                        key={index}
                                                        {...cell.getCellProps()}
                                                    >
                                                        {cell.render('Cell')}
                                                    </BodyTableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={pageNo}
                        count={totalPages}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default FinalScore;
